import React from 'react';

/**
 * Compute automatic page breaks for a given editor height.
 *
 * DOM access is confined to `snapPageBreakFn` which the caller provides.
 * All other inputs are plain values.
 *
 * @param {{
 *   editorHeightInPts: number,
 *   manualPageBreaks: Array,
 *   snapPageBreakFn: (breakObj: object, skipToFirst?: boolean) => void,
 *   editorChildren: Array,
 *   baseHeightInPts: number,
 *   firstAndLastPageHeightInPts: number | null,
 *   firstPageBaseHeightInPts: number | null,
 *   lastPageBaseHeightInPts: number | null,
 *   firstPageMargins: object | null | undefined,
 *   lastPageMargins: object | null | undefined,
 * }}
 * @returns {{
 *   autos: Array,
 *   tempBoundaries: Array,
 *   missingAt: boolean,
 *   currentHeight: number,
 *   lastPage: boolean,
 * }}
 */
export function computeAutoPageBreaks({
	editorHeightInPts,
	manualPageBreaks,
	snapPageBreakFn,
	editorChildren,
	baseHeightInPts,
	firstAndLastPageHeightInPts,
	firstPageBaseHeightInPts,
	lastPageBaseHeightInPts,
	firstPageMargins,
	lastPageMargins,
}) {
	const autos = [];
	const tempBoundaries = [];
	const addedManuals = [];

	let currentHeight = 0;
	let isFirstPage = true;
	let lastPage = false;

	while (currentHeight < editorHeightInPts) {
		const isLastPage = lastPageMargins && ((editorHeightInPts - currentHeight) <= lastPageBaseHeightInPts);

		const pageHeightInPts = isFirstPage && (firstPageMargins && lastPageMargins)
			? firstAndLastPageHeightInPts : isFirstPage && firstPageMargins
				? firstPageBaseHeightInPts : isLastPage ? lastPageBaseHeightInPts : baseHeightInPts;

		const maxPage = lastPageMargins && firstPageMargins && isFirstPage && isLastPage
			? firstAndLastPageHeightInPts
			: lastPageMargins && isFirstPage && isLastPage
				? lastPageBaseHeightInPts
				: lastPageMargins && editorHeightInPts - currentHeight <= lastPageBaseHeightInPts
					? currentHeight + lastPageBaseHeightInPts
					: lastPageMargins && editorHeightInPts - currentHeight > lastPageBaseHeightInPts
						? currentHeight + baseHeightInPts
						: currentHeight + pageHeightInPts;

		const nextManual = manualPageBreaks.find(e => {
			return e.pos > currentHeight && e.pos < maxPage && (addedManuals.length > 0 ? addedManuals[addedManuals.length - 1] !== e.pos : true);
		});

		if (nextManual) {
			tempBoundaries.push(nextManual);
			addedManuals.push(nextManual);
			currentHeight = nextManual.pos;

		} else {
			const newAuto = {
				pos: currentHeight + pageHeightInPts,
				ref: React.createRef(),
				pageBreakId: Math.round(1 + Math.random() * 10000000),
			};

			snapPageBreakFn(newAuto);
			const nodeAtBreak = newAuto.at ? editorChildren[newAuto.at[0]] : null;
			const gapTooSmall = (newAuto.pos - currentHeight) < (pageHeightInPts * 0.35);
			if (currentHeight === newAuto.pos || gapTooSmall || (nodeAtBreak && nodeAtBreak.type === 'footnote-wrapper')) {
				snapPageBreakFn(newAuto, true);
				const newNodeAtBreak = newAuto.at ? editorChildren[newAuto.at[0]] : null;
				if (newNodeAtBreak && newNodeAtBreak.type === 'footnote-wrapper') {
					newAuto.at = [newAuto.at[0] + 1];
				}
			}

			// Guard: if snap did not advance pos, break to prevent infinite loop.
			if (newAuto.pos <= currentHeight) {
				break;
			}

			const repeatedBreak = manualPageBreaks.find(b => b.pos === newAuto.pos);
			if (!repeatedBreak) {
				autos.push(newAuto);
				tempBoundaries.push(newAuto);
			}

			currentHeight = newAuto.pos;
		}

		lastPage = isLastPage && tempBoundaries.length > 0;
		isFirstPage = false;
	}

	// remove last auto boundary (it sits beyond the document end)
	if (!lastPageMargins || (lastPageMargins && lastPage)) {
		autos.pop();
		tempBoundaries.pop();
	}

	const missingAt = lastPageMargins && autos[autos.length - 1] && !autos[autos.length - 1].at
		? true
		: false;

	return { autos, tempBoundaries, missingAt, currentHeight, lastPage };
}
